import * as React from 'react';
import Grid from '@mui/material/Grid2';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import Set from '../models/set';
import Player from '../models/player';
import { colors } from '../theme/colors';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import { useForm, Controller, SubmitHandler, Path } from 'react-hook-form';

import { updateSet } from '../services/set';
import { useSnackbar } from './SnackbarContext';
import { useApiErrorHandler } from '../services/errors';

interface IMyProps {
  SetData: Set;
  AdminSection?: boolean;
  PlayerData?: Player[];
  NotifyDataChanged?: React.Dispatch<React.SetStateAction<boolean>>;
}

interface IEditForm {
  scoreRed: number;
  scoreBlue: number;
  playerRed1: number;
  playerRed2: number;
  playerRed3: number;
  playerRed4: number;
  playerRed5: number;
  playerRed6: number;
  playerBlue1: number;
  playerBlue2: number;
  playerBlue3: number;
  playerBlue4: number;
  playerBlue5: number;
  playerBlue6: number;
}

const SetView: React.FC<IMyProps> = (props: IMyProps) => {
  const theme = useTheme();

  const [editingSet, setEditingSet] = React.useState<boolean>(false);

  const [openDialog, setOpenDialog] = React.useState(false);
  const [dialogFilteredTeam, setDialogFilteredTeam] = React.useState<'c' | 'm'>('c');
  const [editField, setEditField] = React.useState('');
  const [searchQuery, setSearchQuery] = React.useState<string>('');
  // Dummy, always false state to keep the players select closed as we replace
  // them by the custom dialog.
  const [openSelect] = React.useState(false);

  const { control, handleSubmit, setValue } = useForm({
    defaultValues: {
      scoreRed: props.SetData.pointsRed,
      scoreBlue: props.SetData.pointsBlue,
      playerRed1: props.SetData.playersRed[0],
      playerRed2: props.SetData.playersRed[1],
      playerRed3: props.SetData.playersRed[2],
      playerRed4: props.SetData.playersRed[3],
      playerRed5: props.SetData.playersRed[4],
      playerRed6: props.SetData.playersRed[5],
      playerBlue1: props.SetData.playersBlue[0],
      playerBlue2: props.SetData.playersBlue[1],
      playerBlue3: props.SetData.playersBlue[2],
      playerBlue4: props.SetData.playersBlue[3],
      playerBlue5: props.SetData.playersBlue[4],
      playerBlue6: props.SetData.playersBlue[5],
    },
  });

  const { showSnackbar } = useSnackbar();
  const handleApiError = useApiErrorHandler();

  const handleSubmitChanges: SubmitHandler<IEditForm> = async (data) => {
    if (
      props.SetData.pointsRed !== 0 &&
      props.SetData.pointsBlue !== 0 &&
      String(data.scoreRed) === '0' &&
      String(data.scoreBlue) === '0'
    ) {
      alert('Skóre nesmie byť 0:0');
      return;
    }
    try {
      const response = await updateSet(props.SetData.setNumber, { ...data, moveSet: false });
      showSnackbar(response.message, 'success');
      if (props.NotifyDataChanged) props.NotifyDataChanged(true);
      setEditingSet(false);
    } catch (error: unknown) {
      handleApiError(error);
    }
  };

  const handleOpenDialog = (team: 'c' | 'm', fieldName: string) => {
    setEditField(fieldName);
    setDialogFilteredTeam(team);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditField('');
    setDialogFilteredTeam('c');
  };

  const handlePlayerClick = (selectedPlayer: Player) => {
    setValue(editField as Path<IEditForm>, selectedPlayer.id);
    handleCloseDialog();
  };

  function getPlayerColor(team: string): string {
    if (team === 'm') {
      return theme.palette.mode === 'dark' ? colors.defaultBlueDark : colors.defaultBlueLight;
    } else if (team === 'c') {
      return theme.palette.mode === 'dark' ? colors.defaultRedDark : colors.defaultRedLight;
    } else {
      return 'white';
    }
  }

  function filterPlayers(PlayerData: Player[], team: string): Player[] {
    return PlayerData.filter((player) => player.team === team);
  }

  function filterSearchedPlayers(PlayerData: Player[], team: string): Player[] {
    return PlayerData.filter(
      (player) =>
        player.team === team &&
        `${player.name.toLowerCase()} ${player.lastname.toLowerCase()}`.includes(
          searchQuery.toLowerCase(),
        ),
    );
  }

  function sortPlayersByLastName(players: Player[]): Player[] {
    return [...players].sort((a, b) => a.lastname.localeCompare(b.lastname));
  }

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  return (
    <Grid
      container
      spacing={1}
      sx={{
        width: '100%',
        minWidth: 350,
        maxWidth: props.AdminSection ? 450 : 400,
        padding: { xs: 1, md: 1.5, xl: 2 },
        borderRadius: 2,
        border: theme.palette.mode === 'dark' ? colors.gridBorderDark : colors.gridBorderLight,
        bgcolor:
          theme.palette.mode === 'dark' ? colors.gridBackgroundDark : colors.gridBackgroundLight,
      }}
    >
      <Stack
        direction="row"
        spacing={{ xs: 0 }}
        sx={{
          width: '100%',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Paper
          sx={{
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            borderRight: 'none',
            borderRadius: '8px 0 0 8px',
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 'bold', padding: 2 }}>
            {props.SetData.setNumber.toString()}
          </Typography>
        </Paper>
        <TableContainer
          component={Paper}
          sx={{
            margin: 'auto',
            borderLeft: 'none',
            borderRadius: props.AdminSection ? '0 0 0 0' : '0 8px 8px 0',
          }}
        >
          <Table size="small" sx={{ '& td, & th': { padding: '1' } }} data-testid="set-view">
            <TableHead>
              <TableRow>
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: 'bold',
                    pt: '10px',
                    backgroundColor:
                      theme.palette.mode === 'dark'
                        ? colors.defaultRedDark
                        : colors.defaultRedLight,
                  }}
                >
                  Červení
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: 'bold',
                    pt: '10px',
                    backgroundColor:
                      theme.palette.mode === 'dark'
                        ? colors.defaultBlueDark
                        : colors.defaultBlueLight,
                  }}
                >
                  Modrí
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {props.SetData.pointsRed !== 0 || props.SetData.pointsBlue !== 0 ? (
                <TableRow>
                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                    {editingSet ? (
                      <Controller
                        name="scoreRed"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            type="number"
                            inputProps={{ 'data-testid': 'field-points-red' }}
                            slotProps={{
                              inputLabel: {
                                shrink: true,
                              },
                            }}
                            size="small"
                          />
                        )}
                      />
                    ) : (
                      <>{props.SetData.pointsRed}</>
                    )}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                    {editingSet ? (
                      <Controller
                        name="scoreBlue"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            type="number"
                            inputProps={{ 'data-testid': 'field-points-blue' }}
                            slotProps={{
                              inputLabel: {
                                shrink: true,
                              },
                            }}
                            size="small"
                          />
                        )}
                      />
                    ) : (
                      <>{props.SetData.pointsBlue}</>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                <></>
              )}
              {props.SetData.playerNamesRed.map((player, index) => (
                <TableRow key={index}>
                  <TableCell align="center">
                    {editingSet ? (
                      <Controller
                        name={`playerRed${index + 1}` as Path<IEditForm>}
                        key={`playerRed${index + 1}`}
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            variant="standard"
                            disableUnderline={true}
                            defaultValue={props.SetData.playersRed[index]}
                            sx={{ fontSize: 'small' }}
                            open={openSelect}
                            onOpen={() => handleOpenDialog('c', `playerRed${index + 1}`)}
                          >
                            {props.PlayerData &&
                              filterPlayers(props.PlayerData, 'c').map((p) => (
                                <MenuItem value={p.id} key={p.id}>
                                  {p.lastname} {p.name}
                                </MenuItem>
                              ))}
                          </Select>
                        )}
                      />
                    ) : (
                      <>{player}</>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {editingSet ? (
                      <Controller
                        name={`playerBlue${index + 1}` as Path<IEditForm>}
                        key={`playerBlue${index + 1}`}
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            variant="standard"
                            disableUnderline={true}
                            defaultValue={props.SetData.playersBlue[index]}
                            sx={{ fontSize: 'small' }}
                            open={openSelect}
                            onOpen={() => handleOpenDialog('m', `playerBlue${index + 1}`)}
                          >
                            {props.PlayerData &&
                              filterPlayers(props.PlayerData, 'm').map((p) => (
                                <MenuItem value={p.id} key={p.id}>
                                  {p.lastname} {p.name}
                                </MenuItem>
                              ))}
                          </Select>
                        )}
                      />
                    ) : (
                      <>{props.SetData.playerNamesBlue[index]}</>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {props.AdminSection ? (
          <Paper
            sx={{
              height: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              borderRight: 'none',
              borderRadius: '0px 8px 8px 0px',
              padding: 1,
            }}
          >
            {editingSet ? (
              <Stack direction={'column'}>
                <IconButton
                  size="small"
                  color="primary"
                  data-testid="button-submit"
                  onClick={handleSubmit(handleSubmitChanges)}
                >
                  <CheckIcon />
                </IconButton>
                <IconButton
                  size="small"
                  color="secondary"
                  data-testid="button-cancel"
                  onClick={() => setEditingSet(false)}
                >
                  <CloseIcon />
                </IconButton>
              </Stack>
            ) : (
              <Tooltip title="Upraviť výsledok">
                <IconButton
                  size="small"
                  data-testid="button-edit"
                  onClick={() => setEditingSet(true)}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Paper>
        ) : (
          <></>
        )}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="xs" fullWidth>
          <DialogTitle>
            Vyber hráča
            <IconButton
              aria-label="close"
              onClick={handleCloseDialog}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              placeholder="Hľadaj hráča..."
              value={searchQuery}
              onChange={handleSearchChange}
              sx={{ marginBottom: 2 }}
            />
            <TableContainer component={Paper}>
              <Table size="small" sx={{ '& td, & th': { padding: '1' } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Meno</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {props.PlayerData ? (
                    <>
                      {sortPlayersByLastName(
                        filterSearchedPlayers(props.PlayerData, dialogFilteredTeam),
                      ).map((player) => (
                        <TableRow
                          key={player.id}
                          hover
                          onClick={() => handlePlayerClick(player)}
                          sx={{
                            cursor: 'pointer',
                            backgroundColor: getPlayerColor(player.team ?? 'unknown'),
                          }}
                        >
                          <TableCell>{player.lastname + ' ' + player.name}</TableCell>
                        </TableRow>
                      ))}
                    </>
                  ) : (
                    <></>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </DialogContent>
        </Dialog>
      </Stack>
    </Grid>
  );
};

export default SetView;
